import { forwardRef, useContext, useEffect, useState } from 'react';
import { Button, Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { getStyles } from './Answer.style';
import {
	Art,
	Bank,
	Bet,
	CalendarLarge,
	CalendarMid,
	CalendarSmall,
	CalendarXLarge,
	Charity,
	Community,
	Education,
	Electronics,
	Employment,
	Family,
	Friends,
	Fundraiser,
	Gambling,
	Gift,
	GiveMoney,
	Other,
	Hospitality,
	Inheritance,
	Jewels,
	Labor,
	Multiple,
	Pool,
	Prayer,
	SelfEmployment,
	Single,
	Stage,
	Win,
	Repayment,
	Baby,
	Business,
	Casino,
	Cattle,
	Childcare,
	Healthcare,
	Landscaping,
	Money,
	Pet,
	Restaurant,
	Sports,
	ElderCare,
	FamilyCare,
	Investments,
	Produce,
	RealEstate,
	AndroidPhone,
	BenefitsPayment,
	Computer,
	CryptoAtm,
	DownPayment,
	Email,
	Fax,
	GiftCards,
	InPerson,
	Invoice,
	IPhone,
	LegalSettlement,
	MultipleDevices,
	PhoneCall,
	Rent,
	SecurityDeposit,
	Sms,
	Tablet,
	TaxRefund,
	TransferMoney,
	Utilities,
	VideoCall,
	Other2,
} from '../../icons/question/components';
import Hammer from '../../icons/question/components/Hammer';
import Hammer2 from '../../icons/question/components/Hammer2';
import Tie from '../../icons/question/components/Tie';
import SvgHome from '../../icons/question/components/Home';
import SvgEvent from '../../icons/question/components/Event';
import SvgVehicle from '../../icons/question/components/Vehicle';
import { LocalizationContext } from '@context/LocalizationContext';
import { AcceptCheck } from '@icons/question/components/AcceptCheck';
import { NotAcceptCheck } from '@icons/question/components/NotAcceptCheck';
import { NotTheOriginalPayee } from '@icons/NotTheOriginalPayee';
import { IDidNotWriteThisCheck } from '@icons/IDidNotWriteThisCheck';
import { ThisAccountIsNotMine } from '@icons/ThisAccountisNotMine';
import { AnotherIssue } from '@icons/AnotherIssue';
import { OutOfSequence } from '@icons/OutOfSequence';
import { NotTheOriginalStock } from '@icons/NotTheOriginalStock';

interface IAnswerProps {
	text: string;
	index: number;
	icon: string;
	answerId: string;
	disabled: boolean;
	setAnswer: Function;
	isCheckQuestion?: boolean;
	selectedAnswerId: string | null;
}

export const AnswerComponent = forwardRef((props: IAnswerProps, ref: any) => {
	const [selected, setSelected] = useState(false);
	const { A11Y } = useContext(LocalizationContext);

	const theme = useTheme();
	const greaterThanSmall = useMediaQuery(theme.breakpoints.up('sm'));
	const greaterThanMid = useMediaQuery(theme.breakpoints.up('md'));
	const greaterThanLarge = useMediaQuery(theme.breakpoints.up('lg'));
	const greaterThanExtraSmall = useMediaQuery(theme.breakpoints.up('xs'));

	const screenSize = {
		greaterThanMid,
		greaterThanExtraSmall,
		greaterThanSmall,
		greaterThanLarge,
	};

	const styles = getStyles({
		theme,
		screenSize,
		isCheckQuestion: !!props.isCheckQuestion,
		withIcon: !!props.icon,
	});

	useEffect(() => {
		setSelected(props.selectedAnswerId === props.answerId);
	}, [props.selectedAnswerId, props.answerId]);

	const iconLoader = (selected: boolean) => {
		let primaryColor = theme.palette.primary.main;
		let secondaryColor = theme.palette.secondary.main;
		if (!selected) primaryColor = secondaryColor = theme.palette.primary.main; // Unselected color

		if (props.disabled) {
			primaryColor = theme.palette.text.disabled;
			secondaryColor = theme.palette.text.disabled;
		}
		if (!props.icon) return <></>;
		if (props.icon === 'OutOfSequence') return <OutOfSequence />;
		if (props.icon === 'NotTheOriginalStock') return <NotTheOriginalStock />;
		if (props.icon === 'AcceptCheck')
			return <AcceptCheck primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'NotAcceptCheck')
			return <NotAcceptCheck primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'NotTheOriginalPayee') return <NotTheOriginalPayee />;
		if (props.icon === 'IDidNotWriteThisCheck') return <IDidNotWriteThisCheck />;
		if (props.icon === 'ThisAccountIsNotMine') return <ThisAccountIsNotMine />;
		if (props.icon === 'AnotherIssue') return <AnotherIssue />;
		if (props.icon === 'Art') return <Art primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Bank') return <Bank primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Bet') return <Bet primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'CalendarLarge')
			return <CalendarLarge primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'CalendarMid')
			return <CalendarMid primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'CalendarSmall')
			return <CalendarSmall primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'CalendarXLarge')
			return <CalendarXLarge primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Charity') return <Charity primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Community')
			return <Community primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Education')
			return <Education primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Electronics')
			return <Electronics primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Employment')
			return <Employment primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Event') return <SvgEvent primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Family') return <Family primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Friends') return <Friends primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Fundraiser')
			return <Fundraiser primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Gambling') return <Gambling primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Gift') return <Gift primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'GiveMoney')
			return <GiveMoney primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Hammer') return <Hammer primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Hammer2') return <Hammer2 primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Home') return <SvgHome primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Hospitality')
			return <Hospitality primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Inheritance')
			return <Inheritance primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Jewels') return <Jewels primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Labor') return <Labor primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Multiple') return <Multiple primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Other') return <Other primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Pool') return <Pool primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Prayer') return <Prayer primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Repayment')
			return <Repayment primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'SelfEmployment')
			return <SelfEmployment primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Single') return <Single primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Stage') return <Stage primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Tie') return <Tie primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Win') return <Win primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Vehicle') return <SvgVehicle primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Baby') return <Baby primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Business') return <Business primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Casino') return <Casino primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Cattle') return <Cattle primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Childcare')
			return <Childcare primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Elder_care' || props.icon.toLowerCase() === 'eldercare')
			return <ElderCare primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Family_Care' || props.icon.toLowerCase() === 'familycare')
			return <FamilyCare primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Healthcare')
			return <Healthcare primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Investments' || props.icon === 'Invetments')
			return <Investments primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Landscaping')
			return <Landscaping primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Money') return <Money primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Pet') return <Pet primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Restaurant')
			return <Restaurant primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Sports') return <Sports primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon.toLocaleLowerCase() === 'realestate')
			return <RealEstate primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Produce') return <Produce primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'AndroidPhone')
			return <AndroidPhone primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'BenefitsPayment')
			return <BenefitsPayment primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Computer') return <Computer primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'CryptoAtm')
			return <CryptoAtm primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'DownPayment')
			return <DownPayment primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Email') return <Email primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Fax') return <Fax primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'GiftCards')
			return <GiftCards primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'InPerson') return <InPerson primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Invoice') return <Invoice primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'IPhone') return <IPhone primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'LegalSettlement')
			return <LegalSettlement primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'MultipleDevices')
			return <MultipleDevices primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'PhoneCall')
			return <PhoneCall primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Rent') return <Rent primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'SecurityDeposit')
			return <SecurityDeposit primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Sms') return <Sms primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Tablet') return <Tablet primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'TaxRefund')
			return <TaxRefund primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'TransferMoney')
			return <TransferMoney primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Utilities')
			return <Utilities primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'VideoCall')
			return <VideoCall primaryColor={primaryColor} secondaryColor={secondaryColor} />;
		if (props.icon === 'Other2') return <Other2 primaryColor={primaryColor} secondaryColor={secondaryColor} />;
	};

	return (
		<Box ref={ref} data-cy={`pqAnswerButton_id:${props.answerId}`} sx={styles.container}>
			<Button
				aria-label={`${A11Y.AnswerButton} ${props.index} ${props.text}`}
				sx={{
					...(selected ? styles.selectedButton : styles.button),
					...(props.disabled && styles.disabledButton),
				}}
				onClick={() => props.setAnswer(props.answerId, props.text)}
				disabled={props.disabled}>
				<Stack
					overflow={'hidden'}
					gap={{
						xs: '8px',
						sm: '8px',
						md: '16px',
						lg: '16px',
						xl: '16px',
					}}
					justifyContent={'center'}
					alignItems={'center'}
					sx={{ height: '100%', width: '100%' }}>
					<Box sx={styles.icon}>{iconLoader(selected)}</Box>
					<Typography
						component={'span'}
						textOverflow={'ellipsis'}
						textAlign={'center'}
						variant={'answerText'}
						sx={selected ? styles.selectedAnswerText : styles.answerText}>
						{props.text}
					</Typography>
				</Stack>
			</Button>
		</Box>
	);
});
